import { ContractTxAsyncActionResponse } from 'common/models/crypto';
import { AppSyncFarm } from 'common/models/farm';
import { Nullable } from 'common/models/optional';
import {
  ContextWithStringAmount,
  GeneralErrorEvent,
  GetStoredTxResponseEvent,
  XStateEvent,
} from 'common/models/xstate';
import { VaultApi } from 'sdk/vaultApi';
import { CryptoUtils, GAS_SETTINGS } from 'utils/cryptoUtils';
import { InputUtils } from 'utils/inputUtils';
import { NumberUtils } from 'utils/numberUtils';
import { TransactionUtils } from 'utils/transactionUtils';
import { assign, Machine, spawn, interpret } from 'xstate';
import { FarmUserInfo } from 'common/models/token';
import { FarmWithdrawEvent } from './farmWithdrawEvent';
import { XStateUtils } from './xStateUtils';
import { StorageUtils } from 'utils/storageUtils';
import {
  createTxMachine,
  initialTxContext,
  RemoteEventExecutingTx,
  RemoteEventNoStoredTx,
  RemoteEventNoWalletConnected,
  RemoteEventTxError,
  RemoteEventTxSuccess,
  RemoteTxEvent,
} from './txMachine';
import { SwalUtils } from 'utils/swalUtils';
import { ChainId } from 'constants/constants';
import { Address } from 'viem';

enum WithdrawState {
  BASE_STATE = 'BASE_STATE',
  CHECK_INPUT = 'CHECK_INPUT',
  LOADING_DATA = 'LOADING_DATA',
  INVALID_INPUT = 'INVALID_INPUT',
  INSUFFICIENT_LP = 'INSUFFICIENT_LP',
  TOO_MANY_DECIMALS = 'TOO_MANY_DECIMALS',
  CHECKING_FOR_WITHDRAW_TX = 'CHECKING_FOR_WITHDRAW_TX',
  WITHDRAW_AND_GET_TX = 'WITHDRAW_AND_GET_TX',
  HANDLE_WITHDRAW_TX = 'HANDLE_WITHDRAW_TX',
}

type WithdrawSchema = {
  states: {
    [s in WithdrawState]: object;
  };
};

enum WithdrawAction {
  WITHDRAW_ERROR = 'WITHDRAW_ERROR',
  // APPROVE_ERROR = 'APPROVE_ERROR'
}

// TODO: This is duplicated in vaultDepositMachine, should consolidate
enum VaultWithdrawService {
  GET_VAULT_DATA = 'getVaultData',
}

type EventInputChangeArgs = {
  amount: string;
};

export type EventUpdateAccountInfoArgs = {
  address: Address;
  chainId: number;
  farm: AppSyncFarm;
  farmUserInfo?: FarmUserInfo;
};

type EventWithdrawArgs = object;

export type EventLiquidityPercentageChangeArgs = {
  value: number;
};

// NOTE that xstate always whats the value to be named 'value', don't change the name,
// The actual value can be anything, but the variable has to be called value
type EventInputChange = XStateEvent<
  FarmWithdrawEvent.INPUT_CHANGE,
  EventInputChangeArgs
>;
type EventLiquidityPercentChange = XStateEvent<
  FarmWithdrawEvent.LIQUIDITY_PERCENTAGE_CHANGE,
  EventLiquidityPercentageChangeArgs
>;
type EventUpdateAccountInfo = XStateEvent<
  FarmWithdrawEvent.UPDATE_ACCOUNT_INFO,
  EventUpdateAccountInfoArgs
>;
type EventCheckInput = XStateEvent<FarmWithdrawEvent.CHECK_INPUT>;
type EventWithdraw = XStateEvent<FarmWithdrawEvent.WITHDRAW, EventWithdrawArgs>;
type EventLoadData = XStateEvent<FarmWithdrawEvent.LOAD_DATA>;

type FarmWithdrawEvents =
  | EventInputChange
  | EventLiquidityPercentChange
  | EventUpdateAccountInfo
  | EventCheckInput
  | EventWithdraw
  | EventLoadData
  // Remote Tx Events
  | RemoteEventExecutingTx
  | RemoteEventTxError
  | RemoteEventTxSuccess
  | RemoteEventNoWalletConnected
  | RemoteEventNoStoredTx;

type WithdrawContext = {
  accountAddress: Nullable<Address>;
  chainId: Nullable<number>;
  farmUserInfo?: FarmUserInfo;
  availableLP?: string;
  availableLPUSD: number;
  liquidityPercentage: number;
  inputError?: string;
  amount: string;
  amountUsd: number;
  storedTx: Nullable<StorageUtils.StoredTx>;
  farm: Nullable<AppSyncFarm>;
  gasLimit: bigint;
  withdrawResponse: Nullable<ContractTxAsyncActionResponse>;
  withdrawError: Nullable<Error>;
  dataResponse: Nullable<VaultApi.GetVaultDataResponse>;
  dataError: Nullable<Error>;
  isWithdrawing: boolean;
  isLoadingData: boolean;
  txRef: any;
} & ContextWithStringAmount;

const defaultLiquidityPercentage = 25;

export const initialWithdrawContext: WithdrawContext = {
  accountAddress: null,
  chainId: null,
  farmUserInfo: undefined,
  availableLP: '0',
  availableLPUSD: 0,
  liquidityPercentage: defaultLiquidityPercentage,
  inputError: undefined,
  amount: '',
  amountUsd: 0,
  storedTx: null,
  farm: null,
  gasLimit: CryptoUtils.calculateGasMargin(
    BigInt(GAS_SETTINGS.withdraw),
    ChainId.Mainnet
  ),
  withdrawResponse: null,
  withdrawError: null,
  dataResponse: null,
  dataError: null,
  isWithdrawing: false,
  isLoadingData: false,
  txRef: null,
};

const isInsufficientLp = (context: WithdrawContext) => {
  // If we haven't fetched the farm data yet, don't give a false positive
  if (context.dataResponse == null) {
    return false;
  }
  // return context.dataResponse.lpBalanceNumber < Number(context.amount);
  return (
    context.dataResponse.lpBalance < NumberUtils.stringToBN(context.amount, 18n)
  );
};

type GetVaultDataResponseEvent = {
  type: string;
  data: VaultApi.GetVaultDataResponse;
};

const vaultApi = VaultApi.Vault.getInstance();

// https://github.com/ichifarm/ichi-app/blob/master/src/pages/Farm/index.tsx#L488
const vaultWithdrawMachine = Machine<
  WithdrawContext,
  WithdrawSchema,
  FarmWithdrawEvents
>(
  {
    key: 'vaultWithdraw',
    initial: WithdrawState.BASE_STATE,
    context: XStateUtils.shortStringify(initialWithdrawContext),
    states: {
      [WithdrawState.BASE_STATE]: {
        on: {
          [FarmWithdrawEvent.LOAD_DATA]: {
            target: WithdrawState.LOADING_DATA,
          },
          [FarmWithdrawEvent.INPUT_CHANGE]: {
            target: WithdrawState.LOADING_DATA,
            actions: assign({
              amount: (_, event) => event.value.amount,
            }),
          },
          [FarmWithdrawEvent.LIQUIDITY_PERCENTAGE_CHANGE]: {
            target: WithdrawState.CHECK_INPUT,
            actions: assign({
              liquidityPercentage: (_, event) => event.value.value,
              amount: (context, event) =>
                NumberUtils.amountBasedOnPercentage(
                  BigInt(context.availableLP || '0'),
                  event.value.value
                ),
              amountUsd: (context, event) =>
                (context.availableLPUSD * event.value.value) / 100,
            }),
          },
          [FarmWithdrawEvent.UPDATE_ACCOUNT_INFO]: {
            target: WithdrawState.LOADING_DATA,
            actions: assign({
              accountAddress: (_, event: EventUpdateAccountInfo) =>
                event.value.address,
              chainId: (_, event: EventUpdateAccountInfo) =>
                event.value.chainId,
              farm: (_, event: EventUpdateAccountInfo) => event.value.farm,
              availableLP: (_, event) =>
                event.value.farmUserInfo
                  ? NumberUtils.stringToBNString(
                      event.value.farmUserInfo?.availableLP,
                      18n
                    )
                  : '0',
              availableLPUSD: (_, event) =>
                event.value.farmUserInfo
                  ? event.value.farmUserInfo.availableLPUSD
                  : 0,
              amount: (context, event) =>
                NumberUtils.lpToAmountBasedOnPercentage(
                  event.value.farmUserInfo?.availableLP,
                  context.liquidityPercentage
                ),
              amountUsd: (context, event) =>
                ((event.value.farmUserInfo
                  ? event.value.farmUserInfo?.availableLPUSD
                  : 0) *
                  context.liquidityPercentage) /
                100,
            }),
          },
          [FarmWithdrawEvent.WITHDRAW]: {
            target: WithdrawState.WITHDRAW_AND_GET_TX,
          },
        },
      },
      [WithdrawState.CHECKING_FOR_WITHDRAW_TX]: {
        invoke: {
          src: (context) => {
            return TransactionUtils.getStoredTx({
              accountAddress: context.accountAddress as string,
              chainId: context.chainId as number,
              functionName: VaultApi.Vault.withdrawFN,
            });
          },
          // Success means there is a mint tx, re-execute mint
          onDone: {
            target: WithdrawState.HANDLE_WITHDRAW_TX,
            actions: assign({
              storedTx: (_, event: GetStoredTxResponseEvent) => event.data,
            }),
          },
          // Error just means no tx found, go back to waiting for input
          onError: {
            target: WithdrawState.CHECK_INPUT,
          },
        },
      },
      [WithdrawState.WITHDRAW_AND_GET_TX]: {
        entry: assign({
          isWithdrawing: () => true,
        }),
        exit: assign({
          isWithdrawing: () => false,
        }),
        invoke: {
          src: (context) => {
            return vaultApi.withdraw({
              chainId: context.chainId as number,
              farm: context.farm as AppSyncFarm,
              accountAddress: context.accountAddress as Address,
              amount: context.amount,
              amountUsd: context.amountUsd,
            });
          },
          onDone: {
            // Here we aren't going to indicate success as this is 'detached' async response where
            // we have to wait for the actual tx to finish, which is being tracked in localstorage/indexDB
            // See the activities below
            target: WithdrawState.HANDLE_WITHDRAW_TX,
            actions: assign({
              storedTx: (_, event: GetStoredTxResponseEvent) => event.data,
            }),
          },
          onError: {
            target: WithdrawState.BASE_STATE,
            actions: [
              WithdrawAction.WITHDRAW_ERROR,
              assign({
                withdrawError: (_, event: GeneralErrorEvent) => event.data,
                isWithdrawing: () => false,
              }),
            ],
          },
        },
      },
      [WithdrawState.HANDLE_WITHDRAW_TX]: {
        entry: assign({
          isWithdrawing: () => true,
          txRef: (context) =>
            spawn(
              createTxMachine('txMachine', {
                ...initialTxContext,
                chainId: (context as unknown as WithdrawContext).chainId,
                accountAddress: (context as unknown as WithdrawContext)
                  .accountAddress,
                storedTx: (context as unknown as WithdrawContext).storedTx,
              }),
              {
                sync: true,
                name: `tx-machine-${
                  (context as unknown as WithdrawContext).storedTx?.hash
                }`,
              }
            ),
        }),
        exit: assign({
          isWithdrawing: () => false,
        }),
        // These assume the txMachine handles the notifications
        on: {
          [RemoteTxEvent.REMOTE_EXECUTING_TX]: {},
          [RemoteTxEvent.REMOTE_NO_STORED_TX]: {
            target: WithdrawState.BASE_STATE,
          },
          [RemoteTxEvent.REMOTE_NO_WALLET_CONNECTED]: {
            target: WithdrawState.BASE_STATE,
          },
          [RemoteTxEvent.REMOTE_TX_ERROR]: {
            target: WithdrawState.BASE_STATE,
          },
          [RemoteTxEvent.REMOTE_TX_SUCCESS]: {
            // re-'call' the action so the react component can listen and react
            // actions: RemoteTxEvent.REMOTE_TX_SUCCESS,
            actions: [
              RemoteTxEvent.REMOTE_TX_SUCCESS,
              assign({
                amount: () => '0',
                amountUsd: () => 0,
                liquidityPercentage: () => 0,
              }),
            ],
            target: WithdrawState.LOADING_DATA,
          },
        },
      },
      [WithdrawState.LOADING_DATA]: {
        entry: assign({
          isLoadingData: () => true,
        }),
        exit: assign({
          isLoadingData: () => false,
        }),
        invoke: {
          src: VaultWithdrawService.GET_VAULT_DATA,
          onDone: {
            target: WithdrawState.CHECKING_FOR_WITHDRAW_TX,
            actions: assign({
              dataResponse: (_, event: GetVaultDataResponseEvent) => event.data,
            }),
          },
          onError: {
            target: WithdrawState.CHECK_INPUT,
            actions: assign({
              dataError: (_, event: GeneralErrorEvent) => event.data,
            }),
          },
        },
      },
      [WithdrawState.CHECK_INPUT]: {
        always: [
          {
            target: WithdrawState.BASE_STATE,
            cond: InputUtils.isDefaultInput,
          },
          {
            target: WithdrawState.BASE_STATE,
            cond: InputUtils.isZeroInput,
          },
          {
            target: WithdrawState.INSUFFICIENT_LP,
            cond: isInsufficientLp,
          },
          {
            target: WithdrawState.TOO_MANY_DECIMALS,
            cond: InputUtils.isTooManyDecimals18,
          },
          {
            target: WithdrawState.INVALID_INPUT,
            cond: InputUtils.isInvalidInput,
          },
          {
            target: WithdrawState.BASE_STATE,
            cond: InputUtils.isValidInput,
          },
        ],
      },
      [WithdrawState.INVALID_INPUT]: {
        entry: assign({
          inputError: () => 'Invalid Input!',
        }),
        exit: assign({
          inputError: () => undefined,
        }),
        on: {
          [FarmWithdrawEvent.INPUT_CHANGE]: {
            target: WithdrawState.CHECK_INPUT,
            actions: assign({
              amount: (_, event) => event.value.amount,
            }),
          },
          [FarmWithdrawEvent.LIQUIDITY_PERCENTAGE_CHANGE]: {
            target: WithdrawState.CHECK_INPUT,
            actions: assign({
              liquidityPercentage: (_, event) => event.value.value,
              withdrawResponse: () => null,
              amount: (context, event) =>
                NumberUtils.lpToAmountBasedOnPercentage(
                  context.availableLP,
                  event.value.value
                ),
              amountUsd: (context, event) =>
                (context.availableLPUSD * event.value.value) / 100,
            }),
          },
        },
      },
      [WithdrawState.INSUFFICIENT_LP]: {
        entry: assign({
          inputError: () => 'Insufficient LP!',
        }),
        exit: assign({
          inputError: () => undefined,
        }),
        on: {
          [FarmWithdrawEvent.INPUT_CHANGE]: {
            target: WithdrawState.CHECK_INPUT,
            actions: assign({
              amount: (_, event) => event.value.amount,
            }),
          },
          [FarmWithdrawEvent.LIQUIDITY_PERCENTAGE_CHANGE]: {
            target: WithdrawState.CHECK_INPUT,
            actions: assign({
              liquidityPercentage: (_, event) => event.value.value,
              withdrawResponse: () => null,
              amount: (context, event) =>
                NumberUtils.lpToAmountBasedOnPercentage(
                  context.availableLP,
                  event.value.value
                ),
              amountUsd: (context, event) =>
                (context.availableLPUSD * event.value.value) / 100,
            }),
          },
        },
      },
      [WithdrawState.TOO_MANY_DECIMALS]: {
        entry: assign({
          inputError: () => 'Too many decimals!',
        }),
        exit: assign({
          inputError: () => undefined,
        }),
        on: {
          [FarmWithdrawEvent.INPUT_CHANGE]: {
            target: WithdrawState.CHECK_INPUT,
            actions: assign({
              amount: (_, event) => event.value.amount,
            }),
          },
          [FarmWithdrawEvent.LIQUIDITY_PERCENTAGE_CHANGE]: {
            target: WithdrawState.CHECK_INPUT,
            actions: assign({
              liquidityPercentage: (_, event) => event.value.value,
              withdrawResponse: () => null,
              amount: (context, event) =>
                NumberUtils.lpToAmountBasedOnPercentage(
                  context.availableLP,
                  event.value.value
                ),
              amountUsd: (context, event) =>
                (context.availableLPUSD * event.value.value) / 100,
            }),
          },
        },
      },
    },
  },
  {
    services: {
      [VaultWithdrawService.GET_VAULT_DATA]: (context) => {
        return vaultApi.getVaultData({
          accountAddress: context.accountAddress as Address,
          chainId: context.chainId as number,
          farm: context.farm as AppSyncFarm,
          amount: context.amount,
        });
      },
    },
    actions: {
      [WithdrawAction.WITHDRAW_ERROR]: (_, event) => {
        // I wonder if there is a better way to do this
        const evt = event as unknown as GeneralErrorEvent;
        void SwalUtils.ToastErrorTx.fire({
          title:
            evt?.data?.shortMessage ??
            evt?.data?.message ??
            'Error Withdrawing!',
        });
      },
    },
  }
);

export const vaultWithdrawService = interpret(vaultWithdrawMachine, {
  // devTools: process.env.NODE_ENV === 'development'
}).start();

vaultWithdrawService.subscribe((s) => {
  if (process.env.NODE_ENV === 'development') {
    console.debug('[vaultWithdrawService:debug]', s);
  }
});
